// System notification data utilities

export const resolvedOutageData = {
    incidentId: 'INC-20250721-0001',
    description: 'The system downtime has been resolved. All services are now operational and data synchronization has been completed.',
    status: 'Resolved',
    resolvedOn: '2025-07-24',
    impactedServices: [
      'Vital Sign Monitoring',
      'Data Synchronization',
      'Push Notifications',
      'Care Team Communication',
      'Alarm & Alert Generation',
      'Security Incident Detection'
    ],
    requiredActions: [
      'All services are now operational',
      'Data synchronization has been completed',
      'No further action required',
      'Contact support if you experience any issues'
    ],
    supportContact: {
      phone: '1-800-PERIN-HELP',
      email: 'support@perinhealth.com',
      availability: 'Available 24/7 for issues'
    }
};

export const alarmAlertOutageData = {
    incidentId: 'INC-20250610-0002',
    description: 'We are experiencing technical difficulties with alarm and alert generation services. Alerts will not be generated during this outage. Patient monitoring data will continue to be collected, but alerts and alarms will not be triggered.',
    status: 'Investigating',
    estimatedResolution: 'Estimated resolution: 1-3 hours',
    impactedServices: [
      'Alarm & Alert Generation',
      'Alert Notifications',
      'Automated Alarm Triggers',
      'Alert Management System'
    ],
    requiredActions: [
      'Do not rely on automated alerts during this outage',
      'Manually monitor patient vital signs and data',
      'Check patient data more frequently than usual',
      'Contact support if you experience issues',
      'Check back for updates'
    ],
    supportContact: {
      phone: '1-800-PERIN-HELP',
      email: 'support@perinhealth.com',
      availability: 'Available 24/7 for issues'
    }
};

export const securityData = {
    incidentId: 'SEC-20250721-0001',
    description: 'Unusual account activity has been detected. This may be a legitimate login from a new device or location.',
    remediationSteps: [
      'Review recent account activity for any unrecognized logins',
      'Contact support if you notice any suspicious activity',
      'Consider updating your security preferences if needed'
    ],
    securityDetails: [
      'Login from IP: 192.168.1.100',
      'Location: New York, NY',
      'Time: 2025-11-25',
      'Device: Chrome on Windows'
    ],
    supportContact: {
      phone: '1-800-PERIN-HELP',
      email: 'support@perinhealth.com',
      availability: 'Available 24/7 for issues'
    },
    dateIssued: '2025-11-25'
};

// Sample non-critical recall data for testing - replace with actual data from your backend
export const nonCriticalRecallData = {
    recallId: 'RCL-20250610-0043',
    description: 'A non-critical device update has been issued for Perin Health Patch devices used by your patients. This is a recommended update to improve device performance.',
    affectedVersion: 'Perin Health Patch v2.0.8 - v2.0.12',
    impact: 'Affected devices may experience minor performance issues and reduced battery life. Patient monitoring will continue normally, but device optimization is recommended.',
    requiredActions: [
        'Review your patient list to identify those using affected devices',
        'Schedule device updates during routine patient visits',
        'Coordinate with Perin Health support for update logistics',
        'Inform patients about the recommended update',
        'Document update completion in patient records'
    ],
    supportLinks: [
        { text: 'View Affected Patients List', url: '#' },
        { text: 'Contact Perin Health Support', url: 'mailto:support@perinhealth.com' },
        { text: 'Download Update Instructions', url: '#' },
        { text: 'View Full Update Notice', url: '#' }
    ]
};


// Sample recall data for testing - replace with actual data from your backend
export const recallData = {
    recallId: 'RCL-20250610-0042',
    description: 'A critical device recall has been issued for Perin Health Patch devices used by your patients. Immediate action is required.',
    affectedVersion: 'Perin Health Patch v2.1.0 - v2.1.5',
    impact: 'Affected devices may experience intermittent data transmission failures, potentially leading to missed vital sign recordings, delayed medical alerts, and compromised patient monitoring. This could impact the accuracy of patient data and delay critical medical interventions.',
    requiredActions: [
      'Review your patient list to identify those using affected devices',
      'Contact affected patients within 24 hours to schedule device replacement',
      'Coordinate with Perin Health support for device replacement logistics',
      'Monitor affected patients more closely until devices are replaced',
      'Document all communications and actions taken'
    ],
    supportLinks: [
      { text: 'View Affected Patients List', url: '#' },
      { text: 'Contact Perin Health Support', url: 'mailto:support@perinhealth.com' },
      { text: 'Download Patient Communication Template', url: '#' },
      { text: 'View Full FDA Recall Notice', url: '#' }
    ]
  };

export const outageData = {
    title: 'System Downtime - Core Services',
    incidentId: 'INC-20250610-0001',
    description: 'We are experiencing technical difficulties with our core services. Users may experience intermittent connectivity issues and delayed data synchronization.',
    status: 'Investigating',
    estimatedResolution: 'Estimated resolution: 2-4 hours',
    impactedServices: [
      'Vital Sign Monitoring',
      'Data Synchronization',
      'Push Notifications',
      'Care Team Communication',
      'Alert Management',
      'Alarm & Alert Generation'
    ],
    requiredActions: [
      'Do not attempt to use the app for diagnostic purposes until the outage is resolved',
      'Alerts will not be generated during the outage',
      'Monitor for any data synchronization delays',
      'Contact support if you experience issues',
      'Check back for updates'
    ],
    supportContact: {
      phone: '1-800-PERIN-HELP',
      email: 'support@perinhealth.com',
      availability: 'Available 24/7 for issues'
    }
  };

// Helper function to get system notification info
// This can be replaced with API calls to fetch real-time system status
export const detectSystemInfo = () => {
    // Example: Return outage data
    // In production, this would check API for active system notifications
    return {'type': 'critical-recall', 'show': false, 'data': recallData};
};

// Helper function to get all available system notification types
export const getSystemNotificationTypes = () => {
    return [
        'outage',
        'resolved-outage',
        'alarm-alert-outage',
        'security',
        'critical-recall',
        'non-critical-recall'
    ];
};

// Helper function to get system notification data by type
export const getSystemNotificationData = (type) => {
    switch (type) {
        case 'outage':
            return outageData;
        case 'resolved-outage':
            return resolvedOutageData;
        case 'alarm-alert-outage':
            return alarmAlertOutageData;
        case 'security':
            return securityData;
        case 'critical-recall':
            return recallData;
        case 'non-critical-recall':
            return nonCriticalRecallData;
        default:
            return null;
    }
};


export const getColors = (type) => {
  switch (type) {
   
    case 'resolved-outage':
      return {
        background: '#EAFAED',
        color: '#155724',
        border: '#68A691',
      };
    case 'non-critical-recall':
      return {
        background: '#fff3cd',
        color: '#856404',
        border: '#ffeaa7',
      };
    default:
      return {
        background: '#EBB6B666',
        color: '#630F0F',
        border: '#DB7878',
      };
  }
};
